import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const useTabAnimation = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'restock' | 'checkout' | 'inventory'>('restock');
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);

  // Determine active tab based on current route
  useEffect(() => {
    const newActiveTab = location.pathname === '/medication-addition' ? 'restock' :
                        location.pathname === '/checkout-billing' ? 'checkout' :
                        location.pathname === '/inventory' ? 'inventory' : 'restock';
    
    // On initial mount, set the active tab without animation
    if (isInitialMount.current) {
      setActiveTab(newActiveTab);
      isInitialMount.current = false;
      return;
    }
    
    // Only update if we're not currently animating
    if (!isAnimating) {
      setActiveTab(newActiveTab);
    }
  }, [location.pathname, isAnimating]);

  const getTabPosition = () => {
    switch (activeTab) {
      case 'restock':
        return '2px';
      case 'checkout':
        return 'calc(33.33% + 1px)';
      case 'inventory':
        return 'calc(66.66% + 1px)';
      default:
        return '2px';
    }
  };

  const getButtonColor = (tab: 'restock' | 'checkout' | 'inventory') => {
    return activeTab === tab ? 'white' : '#64748b';
  };

  const triggerAnimation = (targetTab: 'restock' | 'checkout' | 'inventory') => {
    console.log('Triggering animation to:', targetTab, 'from:', activeTab, 'isAnimating:', isAnimating);
    
    // Clear any existing timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    
    // Set animation state immediately
    setIsAnimating(true);
    
    // Update active tab immediately for instant visual feedback
    setActiveTab(targetTab);
    
    // Reset animation state after transition completes
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
      console.log('Animation completed for:', targetTab);
    }, 700); // Match the CSS transition duration
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  return {
    activeTab,
    isAnimating,
    getTabPosition,
    getButtonColor,
    setActiveTab,
    triggerAnimation
  };
};
